import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";

import { logMessageParts } from "./logLinks";
import styles from "./ServicesTab.module.css";
import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { EmptyState } from "../../components/EmptyState";
import { isSandboxUnavailable } from "../../data/api/client";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { type LogCollection, useServiceLogs } from "../../data/api/serviceLogs";
import {
  type BackingStoreView,
  type PortView,
  type ProcessView,
  restartAllProcesses,
  restartAllServices,
  restartProcess,
  restartService,
  setPortPublic,
  startProcess,
  startService,
  stopProcess,
  stopService,
  useTaskProcesses,
  useTaskServices,
} from "../../data/api/services";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import type { ServiceStatus } from "../../data/types";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";

const statusClass = (status: ServiceStatus): string =>
  cx(
    styles.status,
    status === "up" && styles.up,
    status === "down" && styles.down,
    status === "starting" && styles.starting,
  );

type RowAction = (id: string) => Promise<unknown>;

type RowEndpoints = {
  readonly restart: RowAction;
  readonly start: RowAction;
  readonly stop: RowAction;
};

// Which endpoints a row's controls call. The collection the row came from is
// the whole discriminator — nothing reads a `kind` field
// (specifications/services.md §Processes and services).
const endpointsFor = (row: Row): RowEndpoints =>
  row.collection === "processes"
    ? { restart: restartProcess, start: startProcess, stop: stopProcess }
    : { restart: restartService, start: startService, stop: stopService };

// One row in the tab, from either collection. A process carries ports; a
// backing store carries the network alias its clients connect to
// (specifications/services.md §Surface).
type Row = {
  readonly collection: LogCollection;
  readonly id: string;
  readonly name: string;
  readonly ports: ReadonlyArray<PortView>;
  readonly status: ServiceStatus;
  readonly subtitle: string;
};

const processRow = (process: ProcessView): Row => ({
  collection: "processes",
  id: process.id,
  name: process.name,
  ports: process.ports,
  status: process.status,
  subtitle:
    process.status === "starting"
      ? "starting…"
      : process.ports.length > 0
        ? process.ports.map((p) => `localhost:${String(p.port)}`).join(" · ")
        : "no ports",
});

const serviceRow = (service: BackingStoreView): Row => ({
  collection: "services",
  id: service.id,
  name: service.name,
  ports: [],
  status: service.status,
  // A backing store has no port of its own: its address is its alias on the
  // session network, which is what goes in a connection string. An instance
  // says which template it came from as well — its alias is synthesized, so
  // nothing else on the row tells it from one the repository declared
  // (`on-demand-services.md` §Services tab).
  subtitle: service.requestedFrom
    ? `${service.networkAlias} · from ${service.requestedFrom}`
    : service.networkAlias,
});

export type ServicesTabProps = {
  /** See `PreviewControls.onSandboxLost` — the same reread, from any row's refusal. */
  readonly onSandboxLost: () => void;
  /**
   * This task's sandbox has been torn down — see `TaskDetail.sandboxReleased`.
   * The rows stay, because their logs are still worth reading; every control
   * that would ask something of the container goes.
   */
  readonly sandboxReleased: boolean;
  readonly taskId: string;
};

export const ServicesTab = ({
  onSandboxLost,
  sandboxReleased,
  taskId,
}: ServicesTabProps) => {
  const processes = useTaskProcesses(taskId);
  const services = useTaskServices(taskId);
  // Toggling a port public is admin-only server-side (services.md §Public
  // preview URLs), so hide the control from viewers who can't use it — and
  // from everyone once there is no process left behind the port to reach.
  const { isAdmin } = useCurrentWorkspace();
  const canManage = isAdmin && !sandboxReleased;
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [restartingAll, setRestartingAll] = useState<string | null>(null);

  const refetch = (): void => {
    processes.refetch();
    services.refetch();
  };

  // Status changes (optimistic from the action, then confirmed by the daemon)
  // arrive on the task scope; refetch to reflect them live. Tunnel open/close
  // fire named tunnel.opened/.closed — refetch so the preview pill flips as
  // soon as the tunnel transitions. `sandbox.declarations.updated` is what
  // fills the pane at all for a tab opened while the task was provisioning.
  useRealtimeScope(`task:${taskId}`, (event) => {
    if (
      event.kind === "process.status.changed" ||
      event.kind === "sandbox.declarations.updated" ||
      event.kind === "service.status.changed" ||
      event.kind === "tunnel.opened" ||
      event.kind === "tunnel.closed" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });

  // A failed action leaves the row where it was — the status the daemon reports
  // is the only one worth showing. The exception is a sandbox that is gone: no
  // status is coming, and these controls should not be here at all.
  const onFailure = (err: unknown): void => {
    if (isSandboxUnavailable(err)) {
      onSandboxLost();
    }
  };

  const act = (id: string, fn: RowAction): void => {
    setBusy(id);
    void fn(id)
      .then(refetch)
      .catch(onFailure)
      .finally(() => setBusy(null));
  };

  const restartAll = (
    key: string,
    fn: (taskId: string) => Promise<unknown>,
  ): void => {
    setRestartingAll(key);
    void fn(taskId)
      .then(refetch)
      .catch(onFailure)
      .finally(() => setRestartingAll(null));
  };

  const loading = processes.loading && services.loading;
  const error = processes.error ?? services.error;
  const processRows = processes.processes.map(processRow);
  const serviceRows = services.services.map(serviceRow);
  const rows = [...processRows, ...serviceRows];

  if (loading && rows.length === 0) {
    return <div className={styles.state}>Loading services…</div>;
  }
  if (error) {
    return (
      <EmptyState
        body="We couldn't reach the server to load this sandbox's services."
        title="Something went sideways."
      />
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        body={
          sandboxReleased
            ? "This task's sandbox is gone, and it declared nothing while it ran."
            : "Processes and services declared in .tinsmith/config.json appear here once the sandbox is running."
        }
        title="Nothing declared yet."
      />
    );
  }

  // The selection key is the row's public_id — two declared names can
  // slug-collide, so never key on the name (per specifications/services.md).
  const active = rows.find((r) => r.id === selected) ?? rows[0]!;
  const activeProcess = processes.processes.find((p) => p.id === active.id);

  // Two groups, and a Restart all per group: restarting your dev servers and
  // restarting Postgres are different blast radii (services.md §Restart all).
  const groups = [
    {
      items: processRows,
      key: "processes" as const,
      label: "Processes",
      restart: restartAllProcesses,
    },
    {
      items: serviceRows,
      key: "services" as const,
      label: "Services",
      restart: restartAllServices,
    },
  ];

  return (
    <div className={styles.pane}>
      <div className={styles.list}>
        {groups.map(
          (g) =>
            g.items.length > 0 && (
              <div key={g.key}>
                <div className={styles.listHead}>
                  <span className={styles.groupLabel}>{g.label}</span>
                  {!sandboxReleased ? (
                    <Button
                      disabled={
                        restartingAll === g.key ||
                        !g.items.some((r) => r.status === "up")
                      }
                      onClick={() => {
                        restartAll(g.key, g.restart);
                      }}
                      size="sm"
                      style={{ marginLeft: "auto" }}
                      variant="ghost"
                    >
                      Restart all
                    </Button>
                  ) : null}
                </div>
                {g.items.map((row) => (
                  <div
                    className={cx(
                      styles.row,
                      row.id === active.id && styles.active,
                    )}
                    key={row.id}
                    onClick={() => {
                      setSelected(row.id);
                    }}
                    role="button"
                    tabIndex={-1}
                  >
                    <span className={statusClass(row.status)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={styles.name}>{row.name}</div>
                      <div className={styles.port}>{row.subtitle}</div>
                    </div>
                    {/* One pill per public http port — a process may serve a
                        public web port and a private debug port. */}
                    {row.ports
                      .filter((p) => p.previewUrl)
                      .map((port) => (
                        <a
                          className={styles.urlPillIcon}
                          href={port.previewUrl!}
                          key={port.id}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          rel="noreferrer"
                          target="_blank"
                          title={`Open ${port.previewUrl!}`}
                        >
                          <Icon.External size={12} />
                        </a>
                      ))}
                    {!sandboxReleased ? (
                      <RowToggle busy={busy === row.id} onAct={act} row={row} />
                    ) : null}
                  </div>
                ))}
              </div>
            ),
        )}
      </div>

      <div className={styles.log}>
        <div className={styles.logHead}>
          <span className={statusClass(active.status)} />
          <span className={styles.logTitle}>{active.name}</span>
          <span className="muted mono" style={{ fontSize: 11.5 }}>
            · {active.subtitle}
          </span>
          <span
            style={{
              alignItems: "center",
              display: "flex",
              gap: 10,
              marginLeft: "auto",
            }}
          >
            {activeProcess ? (
              <PortControls
                canManage={canManage}
                onToggle={(portId, isPublic) => {
                  act(portId, (id) => setPortPublic(id, isPublic));
                }}
                ports={activeProcess.ports}
              />
            ) : null}
            {!sandboxReleased ? (
              <RowControls busy={busy === active.id} onAct={act} row={active} />
            ) : null}
          </span>
        </div>
        <ServiceLogPane
          key={active.id}
          row={active}
          sandboxReleased={sandboxReleased}
          taskId={taskId}
        />
      </div>
    </div>
  );
};

const LEVEL_CLASS: Record<"error" | "info" | "warn", string> = {
  error: styles.lvlError!,
  info: styles.lvlInfo!,
  warn: styles.lvlWarn!,
};

// Per-port public toggle. Only an http port can be fronted by the preview
// proxy, so a tcp port shows its number and nothing to toggle.
const PortControls = ({
  canManage,
  onToggle,
  ports,
}: {
  readonly canManage: boolean;
  readonly onToggle: (portId: string, isPublic: boolean) => void;
  readonly ports: ReadonlyArray<PortView>;
}) => (
  <>
    {ports.map((port) =>
      port.kind === "http" ? (
        canManage ? (
          <Checkbox
            checked={port.isPublic}
            key={port.id}
            onCheckedChange={(v) => {
              onToggle(port.id, v);
            }}
          >
            Public :{port.port}
          </Checkbox>
        ) : port.isPublic ? (
          <span className={styles.logHeadPill} key={port.id}>
            Public :{port.port}
          </span>
        ) : null
      ) : (
        <span className={styles.logHeadPill} key={port.id}>
          tcp :{port.port}
        </span>
      ),
    )}
  </>
);

// The follow-tail log pane (specifications/services.md §Log pane / §Log-pane
// states). Backfill + live appends while `up`; a read-only stopped banner while
// `down`. Following auto-scrolls to the newest line; a user scroll-up releases
// it, and toggling Following back on re-pins.
const ServiceLogPane = ({
  row,
  sandboxReleased,
  taskId,
}: {
  readonly row: Row;
  readonly sandboxReleased: boolean;
  readonly taskId: string;
}) => {
  const { hasOlder, lines, loadingOlder, loadOlder } = useServiceLogs(
    taskId,
    row.id,
    row.collection,
  );
  const [following, setFollowing] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Pre-prepend scrollHeight, set when a scroll-up triggers loadOlder so the
  // view stays anchored to the same line once the archived chunk renders
  // (mirrors ChatTab's older-page anchoring).
  const restoreRef = useRef<number | null>(null);

  useEffect(() => {
    if (following && scrollRef.current && restoreRef.current === null) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, following]);

  // Anchor scroll after an archived chunk prepends (before paint, so the
  // prepend never reads as a jump).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && restoreRef.current !== null) {
      el.scrollTop += el.scrollHeight - restoreRef.current;
      restoreRef.current = null;
    }
  }, [lines]);

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (!atBottom && following) {
      setFollowing(false);
    }
    // Scrolled past the live window, into the archived deep history
    // (services.md §Logs: deep history) — pull the next chunk back.
    if (el.scrollTop < 120 && hasOlder && !loadingOlder) {
      restoreRef.current = el.scrollHeight;
      loadOlder();
    }
  };

  return (
    <>
      {sandboxReleased ? (
        <div className={styles.logBanner}>
          This task&rsquo;s sandbox is gone — these are its last logs.
        </div>
      ) : row.status === "down" ? (
        <div className={styles.logBanner}>
          This is stopped — start it to stream logs.
        </div>
      ) : null}
      <div className={styles.logBody} onScroll={onScroll} ref={scrollRef}>
        {loadingOlder ? (
          <div className={styles.state}>Loading earlier logs…</div>
        ) : null}
        {lines.length === 0 ? (
          <div className={styles.state}>
            {row.status === "up" ? "Waiting for output…" : "No log lines yet."}
          </div>
        ) : (
          lines.map((l) => (
            <div
              className={cx(
                styles.line,
                l.level === "warn" && styles.warn,
                l.level === "error" && styles.error,
              )}
              key={l.seq}
            >
              <span className={styles.lineTs}>
                {new Date(l.at).toTimeString().slice(0, 8)}
              </span>
              <span className={cx(styles.lineLvl, LEVEL_CLASS[l.level])}>
                {l.level}
              </span>
              <span className={styles.msg}>
                <LogMessage message={l.message} />
              </span>
            </div>
          ))
        )}
      </div>
      <div className={styles.logFoot}>
        <Checkbox checked={following} onCheckedChange={setFollowing}>
          Follow tail
        </Checkbox>
      </div>
    </>
  );
};

// A message stays text, which React escapes; only its http(s) URLs become
// links.
const LogMessage = ({ message }: { readonly message: string }) => (
  <>
    {logMessageParts(message).map((part) =>
      part.link ? (
        <a
          className={styles.logLink}
          href={part.text}
          key={part.offset}
          rel="noopener noreferrer"
          target="_blank"
        >
          {part.text}
        </a>
      ) : (
        <Fragment key={part.offset}>{part.text}</Fragment>
      ),
    )}
  </>
);

// The list's own start/stop, so a row can be run without being selected first
// (specifications/services.md §Surface — every row carries the control set, not
// just the one whose logs are open). Restart stays in the log head: it is the
// third control, and a list row has room for one.
const RowToggle = ({
  busy,
  onAct,
  row,
}: {
  readonly busy: boolean;
  readonly onAct: (id: string, fn: RowAction) => void;
  readonly row: Row;
}) => {
  const { start, stop } = endpointsFor(row);
  const running = row.status === "up";
  const action = running
    ? { fn: stop, glyph: <Icon.Stop size={12} />, label: `Stop ${row.name}` }
    : { fn: start, glyph: <Icon.Play size={12} />, label: `Start ${row.name}` };

  return (
    <Button
      disabled={busy || row.status === "starting"}
      icon
      onClick={(e) => {
        // The row itself is the selection control; this button is not.
        e.stopPropagation();
        onAct(row.id, action.fn);
      }}
      size="sm"
      title={row.status === "starting" ? `Starting ${row.name}…` : action.label}
      variant="ghost"
    >
      {action.glyph}
    </Button>
  );
};

// Control set aligned to the endpoints (specifications/services.md §Surface):
// Start when down, Stop + Restart when up, a disabled affordance when starting.
// Which endpoint depends on the collection the row came from.
const RowControls = ({
  busy,
  onAct,
  row,
}: {
  readonly busy: boolean;
  readonly onAct: (id: string, fn: RowAction) => void;
  readonly row: Row;
}) => {
  const { restart, start, stop } = endpointsFor(row);

  if (row.status === "starting") {
    return (
      <Button disabled size="sm" variant="ghost">
        Starting…
      </Button>
    );
  }
  if (row.status === "up") {
    return (
      <>
        <Button
          disabled={busy}
          onClick={() => {
            onAct(row.id, restart);
          }}
          size="sm"
          variant="ghost"
        >
          Restart
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            onAct(row.id, stop);
          }}
          size="sm"
          variant="danger"
        >
          Stop
        </Button>
      </>
    );
  }
  return (
    <Button
      disabled={busy}
      onClick={() => {
        onAct(row.id, start);
      }}
      size="sm"
      variant="accent"
    >
      Start
    </Button>
  );
};
